import React from 'react';
import { useAddress } from '../../../hooks/useAddress';
import { CrudStyles } from '../Crud';
import AddressNameField from '../AddressNameField';

const { FormGroup, Input, InputGroup } = CrudStyles;

/**
 * Componente reutilizável para formulário de endereço
 * @param {Object} props
 * @param {Object} props.initialAddress - Endereço inicial (será atualizado quando mudar)
 * @param {Function} props.onChange - Callback quando endereço muda (recebe objeto address)
 * @param {Object} props.errors - Objeto com erros por campo
 * @param {boolean} props.showLabels - Se deve mostrar labels (padrão: true)
 * @param {boolean} props.required - Se campos são obrigatórios (padrão: true)
 * @param {string|number} props.customerId - ID do cliente (para empresa de transporte)
 */
export default function AddressForm({
    initialAddress = {},
    onChange,
    errors = {},
    showLabels = true,
    required = true,
    customerId = null,
}) {
    const {
        address,
        addressLocked,
        loadingAddress,
        handleAddressChange,
        setAddress: setAddressData,
    } = useAddress(initialAddress, customerId);

    // Ref para rastrear o último initialAddress processado e evitar loops
    const lastInitialAddressRef = React.useRef(null);
    const isInitialMount = React.useRef(true);
    
    // Atualiza o endereço quando initialAddress muda externamente (ex: quando dados são carregados na edição)
    // Só atualiza se houver mudanças significativas para evitar loops infinitos
    React.useEffect(() => {
        // Ignora na primeira renderização
        if (isInitialMount.current) {
            isInitialMount.current = false;
            lastInitialAddressRef.current = initialAddress;
            return;
        }
        
        // Compara por referência para evitar loops
        if (lastInitialAddressRef.current === initialAddress) {
            return;
        }
        
        if (initialAddress && (
            initialAddress.zipCode || 
            initialAddress.street || 
            initialAddress.addressId ||
            initialAddress.number ||
            initialAddress.district ||
            initialAddress.city ||
            initialAddress.stateAbbr
        )) {
            // Verifica se há mudanças significativas antes de atualizar
            const hasChanges = 
                (initialAddress.zipCode && initialAddress.zipCode !== address.zipCode) ||
                (initialAddress.street && initialAddress.street !== address.street) ||
                (initialAddress.addressId && initialAddress.addressId !== address.addressId) ||
                (initialAddress.number && initialAddress.number !== address.number) ||
                (initialAddress.complement !== undefined && initialAddress.complement !== address.complement) ||
                (initialAddress.district && initialAddress.district !== address.district) ||
                (initialAddress.city && initialAddress.city !== address.city) ||
                (initialAddress.stateAbbr && initialAddress.stateAbbr !== address.stateAbbr) ||
                (initialAddress.name !== undefined && initialAddress.name !== address.name);
            
            if (hasChanges) {
                // Força atualização do endereço apenas se realmente mudou
                lastInitialAddressRef.current = initialAddress;
                setAddressData(initialAddress);
            } else {
                lastInitialAddressRef.current = initialAddress;
            }
        } else {
            lastInitialAddressRef.current = initialAddress;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- omit address/setAddressData para evitar loops de sincronização
    }, [
        initialAddress?.zipCode,
        initialAddress?.street,
        initialAddress?.addressId,
        initialAddress?.number,
        initialAddress?.district,
        initialAddress?.city,
        initialAddress?.stateAbbr,
        initialAddress?.name,
    ]);

    // Notifica mudanças no endereço
    React.useEffect(() => {
        if (onChange) {
            onChange(address);
        }
    }, [address, onChange]);

    return (
        <>
            <AddressNameField
                value={address.name}
                onChange={handleAddressChange}
                error={errors.addressName}
            />
            <InputGroup>
                <FormGroup flex={0.6}>
                    {showLabels && <label htmlFor="zipCode">CEP {required && '*'}</label>}
                    <Input
                        id="zipCode"
                        name="zipCode"
                        value={address.zipCode}
                        onChange={handleAddressChange}
                        $hasError={!!errors.zipCode}
                        placeholder="00000-000"
                        maxLength={9}
                        disabled={false}
                    />
                    {errors.zipCode && (
                        <span className="error">{errors.zipCode}</span>
                    )}
                </FormGroup>
                <FormGroup flex={1.4}>
                    {showLabels && <label htmlFor="street">Rua {required && '*'}</label>}
                    <Input
                        id="street"
                        name="street"
                        value={address.street}
                        onChange={handleAddressChange}
                        $hasError={!!errors.street}
                        disabled={addressLocked || loadingAddress}
                    />
                    {errors.street && (
                        <span className="error">{errors.street}</span>
                    )}
                </FormGroup>
            </InputGroup>

            <InputGroup>
                <FormGroup flex={0.5}>
                    {showLabels && <label htmlFor="number">Número {required && '*'}</label>}
                    <Input
                        id="number"
                        name="number"
                        value={address.number}
                        onChange={handleAddressChange}
                        $hasError={!!errors.number}
                    />
                    {errors.number && (
                        <span className="error">{errors.number}</span>
                    )}
                </FormGroup>
                <FormGroup flex={1.5}>
                    {showLabels && <label htmlFor="complement">Complemento</label>}
                    <Input
                        id="complement"
                        name="complement"
                        value={address.complement}
                        onChange={handleAddressChange}
                        $hasError={!!errors.complement}
                    />
                    {errors.complement && (
                        <span className="error">{errors.complement}</span>
                    )}
                </FormGroup>
            </InputGroup>

            <InputGroup>
                <FormGroup flex={1.5}>
                    {showLabels && <label htmlFor="district">Bairro {required && '*'}</label>}
                    <Input
                        id="district"
                        name="district"
                        value={address.district}
                        onChange={handleAddressChange}
                        $hasError={!!errors.district}
                        disabled={addressLocked || loadingAddress}
                    />
                    {errors.district && (
                        <span className="error">{errors.district}</span>
                    )}
                </FormGroup>
                <FormGroup flex={1}>
                    {showLabels && <label htmlFor="city">Cidade {required && '*'}</label>}
                    <Input
                        id="city"
                        name="city"
                        value={address.city}
                        onChange={handleAddressChange}
                        $hasError={!!errors.city}
                        disabled={addressLocked || loadingAddress}
                    />
                    {errors.city && (
                        <span className="error">{errors.city}</span>
                    )}
                </FormGroup>
                <FormGroup flex={0.5}>
                    {showLabels && <label htmlFor="stateAbbr">UF {required && '*'}</label>}
                    <Input
                        id="stateAbbr"
                        name="stateAbbr"
                        value={address.stateAbbr}
                        onChange={handleAddressChange}
                        $hasError={!!errors.stateAbbr}
                        disabled={addressLocked || loadingAddress}
                        maxLength={2}
                    />
                    {errors.stateAbbr && (
                        <span className="error">{errors.stateAbbr}</span>
                    )}
                </FormGroup>
            </InputGroup>
        </>
    );
}
